import React, {FC} from "react";
import {FeOrder} from "../../redux/types";
import {Card, CardContent, CardProps, Divider} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {EditableDate} from "../../../../sharedComponents/EditableDate";
import {useUpdateOrder} from "../../redux/orders.hook";
import {LabeledTextDisplay} from "../../../../sharedComponents/LabeledTextDisplay";
import {EditableOrderAddress} from "./EditableOrderAddress";
import {WorkerAssignment} from "./WorkerAssignment";
import {EditableServicesChips} from "../../../services/components/EditableServicesChips";
import {BilledCheckbox} from "../../form/BilledCheckbox";
import {EditableTextField} from "../../../../sharedComponents/EditableTextfield";
import {ConfirmedCheckbox} from "../../form/ConfirmedCheckbox";

const useStyles = makeStyles((theme) => ({
    spacingBottom: {
        marginBottom: theme.spacing(2)
    },
    spacingRight: {
        marginRight: theme.spacing(3)
    }
}));

interface OrderCardProps {
    order?: FeOrder;
    cardProps?: CardProps
}

export const OrderCard: FC<OrderCardProps> = ({order, cardProps}) => {

    const classes = useStyles();

    const {updateOrder} = useUpdateOrder();

    return (
        <Card {...cardProps}>
            <CardContent>
                <div style={{display: 'flex', alignItems: 'center'}} className={classes.spacingBottom}>
                    <EditableDate
                        required
                        disablePast={false}
                        value={order?.estimatedFrom}
                        label={"Von (erwartet)"}
                        editorIdentifier={"estimatedFrom"}
                        onSubmit={(estimatedFrom) => updateOrder(order?.id ?? "", {estimatedFrom})}
                        className={classes.spacingRight}
                    />
                    <EditableDate
                        required
                        moreThan={order?.estimatedFrom}
                        disablePast={false}
                        value={order?.estimatedTo}
                        label={"Bis (erwartet)"}
                        editorIdentifier={"estimatedTo"}
                        onSubmit={(estimatedTo) => updateOrder(order?.id ?? "", {estimatedTo})}
                        className={classes.spacingRight}
                    />
                    {order &&
                    <LabeledTextDisplay
                        label={"Dauer (erwartet)"}
                        suffix=" h"
                        value={Math.abs(order.estimatedTo - order.estimatedFrom) / 36e5}
                    />}
                </div>
                <div style={{display: 'flex', alignItems: 'center'}} className={classes.spacingBottom}>
                    <EditableDate
                        disablePast={false}
                        value={order?.from}
                        label={"Von (ist)"}
                        editorIdentifier={"from"}
                        onSubmit={(from) => updateOrder(order?.id ?? "", {from})}
                        className={classes.spacingRight}
                    />
                    <EditableDate
                        value={order?.to}
                        moreThan={order?.from ?? undefined}
                        disablePast={false}
                        label={"Bis (ist)"}
                        editorIdentifier={"to"}
                        onSubmit={(to) => updateOrder(order?.id ?? "", {to})}
                        className={classes.spacingRight}
                    />
                    {(order?.from && order?.to) &&
                    <LabeledTextDisplay
                        label={"Dauer (ist)"}
                        suffix=" h"
                        value={Math.abs(order.to - order.from) / 36e5}
                    />}
                </div>
                <Divider className={classes.spacingBottom}/>
                <WorkerAssignment
                    workerId={order?.workerId}
                    editorIdentifier="worker"
                    assignWorker={(workerId) => updateOrder(order?.id ?? '', {workerId})}
                    className={classes.spacingBottom}
                />
                <EditableOrderAddress
                    editIdentifier={"serviceAddress"}
                    value={order?.serviceAddress}
                    onSubmit={(serviceAddress) => updateOrder(order?.id ?? '', {serviceAddress})}
                    className={classes.spacingBottom}
                />
                <EditableOrderAddress
                    isInvoiceAddress
                    editIdentifier={"invoiceAddress"}
                    value={order?.invoiceAddress || order?.serviceAddress}
                    onSubmit={(invoiceAddress) => updateOrder(order?.id ?? '', {invoiceAddress})}
                    className={classes.spacingBottom}
                />
                <Divider className={classes.spacingBottom}/>
                <EditableServicesChips
                    editorIdentifier={"services"}
                    label={"Dienstleistungen"}
                    serviceIds={order?.servicesIds ?? []}
                    onSubmit={(servicesIds) => updateOrder(order?.id ?? '', {servicesIds})}
                    className={classes.spacingBottom}
                />
                <ConfirmedCheckbox
                    confirmed={order?.confirmed}
                    onChange={(confirmed) => updateOrder(order?.id ?? '', {confirmed})}
                    className={classes.spacingBottom}
                />
                <BilledCheckbox
                    billed={order?.billed}
                    onChange={(billed) => updateOrder(order?.id ?? '', {billed})}
                    className={classes.spacingBottom}
                />
                <Divider className={classes.spacingBottom}/>
                <EditableTextField
                    formProps={{
                        label: 'Nachricht an Mitarbeiter',
                        initialValue: order?.extraInfo,
                        onSubmit:(extraInfo) => updateOrder(order?.id ?? '', {extraInfo: extraInfo as string}),
                        multiline: true
                    }}
                    editIdentifier="extraInfo"
                />
            </CardContent>
        </Card>
    )
}
